import { Link, useSearchParams } from 'react-router-dom';
import { useEffect, useState, useMemo } from 'react';
import SEOMeta from '@/components/feature/SEOMeta';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { categories } from '@/mocks/categories';
import { articles, getArticlesByCategory } from '@/mocks/articles';

export default function CategoryPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = searchParams.get('tab') || 'agricultural';
  const [isChanging, setIsChanging] = useState(false);

  const activeCategory = useMemo(
    () => categories.find((c) => c.id === activeTab),
    [activeTab]
  );

  const filteredArticles = useMemo(
    () => getArticlesByCategory(activeTab),
    [activeTab]
  );

  const handleTabChange = (tabId: string) => {
    if (tabId === activeTab) return;
    setIsChanging(true);
    setSearchParams({ tab: tabId });
    window.scrollTo({ top: 0, behavior: 'smooth' });
    setTimeout(() => setIsChanging(false), 300);
  };

  const catTitle = activeCategory?.name || '设备分类';

  return (
    <>
      <SEOMeta
        title={`${catTitle} - 喷水设备分类`}
        description={activeCategory?.description || '喷水设备分类页面，覆盖农业喷水灌溉、景观喷水装置、工业喷水冷却三大领域。'}
        keywords={`喷水,喷水设备类型,${catTitle},农业喷水灌溉,景观喷水装置,工业喷水冷却`}
        canonicalUrl={`https://www.penshuixitong.cn/category?tab=${activeTab}`}
      />

      <Navbar />

      <main className="pt-16">
        <div className="bg-background-100">
          <div className="max-w-7xl mx-auto px-4 md:px-6 pt-8">
            <Breadcrumb
              items={[
                { label: '首页', href: '/' },
                { label: '设备分类' },
              ]}
            />
            <div className="py-8">
              <h1 className="font-heading text-2xl md:text-3xl font-semibold text-foreground-900 mb-3">
                喷水设备分类
              </h1>
              <p className="text-sm md:text-base text-foreground-600">
                浏览不同领域的喷水系统方案，找到最适合您的喷水产品
              </p>
            </div>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-4 md:px-6 py-10">
          <div className="flex flex-wrap gap-3 mb-10">
            {categories.map((cat) => (
              <button
                key={cat.id}
                onClick={() => handleTabChange(cat.id)}
                className={`whitespace-nowrap cursor-pointer px-6 py-2.5 rounded-full text-sm font-medium transition-all duration-200 flex items-center gap-2 ${
                  activeTab === cat.id
                    ? 'bg-primary-500 text-background-50'
                    : 'bg-background-100 text-foreground-700 hover:bg-background-200 border border-background-200/70'
                }`}
              >
                <span className="w-5 h-5 flex items-center justify-center">
                  <i className={`${cat.icon} text-sm`}></i>
                </span>
                {cat.name}
                <span className={`text-xs ${activeTab === cat.id ? 'text-background-50/70' : 'text-foreground-400'}`}>
                  ({cat.articleCount})
                </span>
              </button>
            ))}
          </div>

          {activeCategory && (
            <div className="mb-10 p-6 rounded-lg bg-background-100/80 border border-background-200/70">
              <div className="flex flex-col md:flex-row gap-6 items-start">
                <div className="w-full md:w-72 h-44 rounded-lg overflow-hidden flex-shrink-0">
                  <img
                    src={activeCategory.image}
                    alt={activeCategory.name}
                    className="w-full h-full object-cover object-top"
                  />
                </div>
                <div>
                  <h2 className="font-heading text-lg font-semibold text-foreground-900 mb-2">
                    {activeCategory.name}
                  </h2>
                  <p className="text-sm text-foreground-600 leading-relaxed">
                    {activeCategory.description}
                  </p>
                </div>
              </div>
            </div>
          )}

          <div className={`transition-opacity duration-300 ${isChanging ? 'opacity-30' : 'opacity-100'}`}>
            {filteredArticles.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
                {filteredArticles.map((article) => (
                  <Link
                    key={article.id}
                    to={`/article/${article.id}`}
                    className="group block rounded-lg overflow-hidden bg-background-50 border border-background-200/70 hover:border-primary-300 transition-all duration-300 cursor-pointer"
                  >
                    <div className="relative h-48 overflow-hidden">
                      <img
                        src={article.coverImage}
                        alt={article.title}
                        className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                      />
                    </div>
                    <div className="p-5">
                      <h3 className="font-heading text-base font-semibold text-foreground-900 mb-2 line-clamp-2 group-hover:text-primary-500 transition-colors">
                        {article.title}
                      </h3>
                      <p className="text-sm text-foreground-600 leading-relaxed line-clamp-2 mb-3">
                        {article.excerpt}
                      </p>
                      <div className="flex items-center flex-wrap gap-3 text-xs text-foreground-500">
                        <span>{article.author}</span>
                        <span>{article.publishDate}</span>
                        <span>{article.readTime}</span>
                      </div>
                      <div className="mt-3 flex flex-wrap gap-2">
                        {article.tags.slice(0, 3).map((tag, i) => (
                          <span key={i} className="px-2 py-0.5 rounded-full bg-secondary-100 text-secondary-700 text-xs">
                            {tag}
                          </span>
                        ))}
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            ) : (
              <div className="text-center py-16">
                <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-background-100 flex items-center justify-center">
                  <i className="ri-file-search-line text-2xl text-foreground-400"></i>
                </div>
                <p className="text-foreground-600">该分类暂无文章，敬请期待更多喷水系统内容</p>
              </div>
            )}
          </div>
        </div>
      </main>

      <Footer />
      <ScrollToTop />
    </>
  );
}